import { removeExtension } from "../src/taskpane/components/helpers/stringHelper";
import { SiteItem, documentLibraryItem } from "../src/taskpane/redux/models/reduxModels";
import { getItemCustomSelect, getListItemCustomSelect, getSharePointSite } from "../src/taskpane/services/GraphService";
import { getItemVersions, getListFields } from "../src/taskpane/services/SharePointRestServices";
import authModule from "./AuthModule";
import { logger } from "./logger";

const fileNameInternalName: string = "FileLeafRef";
export const versionLabelInternalName: string = "VersionLabel";

export const getFileNameDownloadTemplateAvailable = (siteInfo: SiteItem[]): boolean => {
    if (Array.isArray(siteInfo) && siteInfo.length > 0) {
        const documentLibraries: documentLibraryItem[] = siteInfo.reduce((acc, current) => [...acc, ...current.documentLibraries], []);
        if (documentLibraries?.some(lib => lib.fileNameDownloadFormat?.trim())) {
            return true;
        }
    }
    return false;
}

export const getFileNameDownloadTemplate = (siteInfo: SiteItem[], siteDisplayName: string, documentLibraryDisplayName: string): string | null | undefined => {
    if (Array.isArray(siteInfo) && siteInfo.length > 0) {
        try {
            const sites: SiteItem[] = siteInfo.filter(site => site.displayName?.trim().toLowerCase() === siteDisplayName?.trim().toLowerCase());
            const documentLibraries: documentLibraryItem[] = sites.reduce((acc, current) => [...acc, ...current.documentLibraries], []);
            const documentLibrary: documentLibraryItem = documentLibraries.find(item => item.title?.trim().toLowerCase() === documentLibraryDisplayName?.trim().toLowerCase());
            return documentLibrary?.fileNameDownloadFormat?.trim();
        } catch (error) {
            logger(error, "FileNameDownloadTemplateHelper.ts (getFileNameDownloadTemplate)");
        }
    }
    return null;
};

export const getWildcardValues = (templateString: string): string[] => {
    const widcardValues: string[] = [];

    templateString?.split("<<[").forEach(s => {
        const endIndex: number = s?.indexOf("]>>");
        if (endIndex !== -1) {
            const wildcardValue: string = s.substring(0, endIndex)?.trim();
            if (wildcardValue) {
                widcardValues.push(s.substring(0, endIndex)?.trim());
            }
        }
    });

    return widcardValues;
}

export const getFilenameFromTemplate = async (siteInfo: SiteItem[], driveId: string, itemId: string, siteUrl: string, listId: string, itemUniqueId: string, overrideExtension?: string) => {
    let fileName: string;
    try {
        if (getFileNameDownloadTemplateAvailable(siteInfo)) {
            const { siteDisplayName, libraryDisplayName } = await getContainerDisplayNames(driveId, itemId);
            const fileNameTemplateString: string = siteDisplayName && libraryDisplayName && getFileNameDownloadTemplate(siteInfo, siteDisplayName, libraryDisplayName);
            let wildcardValues: string[];
            if (fileNameTemplateString) {
                wildcardValues = getWildcardValues(fileNameTemplateString);
                if (fileNameTemplateString && Array.isArray(wildcardValues) && wildcardValues.length > 0) {
                    const { listFields, listItem } = await getItemDetails(siteUrl, listId, itemUniqueId);
                    if (listFields && listItem) {
                        fileName = fileNameTemplateString;
                        wildcardValues.forEach(wildcardValue => {
                            const field = wildcardValue === versionLabelInternalName
                                ? { InternalName: versionLabelInternalName }
                                : listFields.value.find(x => x.Title === wildcardValue || x.InternalName === wildcardValue);

                            fileName = fileName.replaceAll(`<<[${wildcardValue}]>>`, getItemFieldTextValue(listItem, field) ?? "");
                        });
                        fileName += "." + (overrideExtension ?? listItem["File_x005f_x0020_x005f_Type"]);
                    }
                }
            }
        }
    } catch (error) {
        logger(error, "FileNameDownloadTemplateHelper.ts (getFilenameFromTemplate)");
    }
    return fileName;
}

export const getItemCurrentVersion = async (accessToken: string, siteUrl: string, listId: string, itemUniqueId: string) => {
    try {
        const itemVersions = (await getItemVersions(accessToken, siteUrl, listId, itemUniqueId))?.value;
        if (Array.isArray(itemVersions)) {
            return itemVersions.find(v => v.IsCurrentVersion);
        }
    } catch (error) {
        logger(error, "FileNameDownloadTemplateHelper.ts (getItemCurrentVersion)");
    }
    return null;
}

export const getItemFieldTextValue = (item: any, field: any): string | null => {
    if (item && field) {
        try {
            const fieldType: string = field.TypeAsString;
            const fieldValue = item[field.InternalName] ?? item[field.InternalName?.replaceAll("_", "_x005f_")];

            if (field.InternalName === fileNameInternalName) {
                return removeExtension(fieldValue);
            }

            if (!fieldType || fieldType === "Text" || fieldType === "Note" || fieldType === "Choice") {
                return fieldValue;
            }
            if (fieldType === "MultiChoice") {
                if (Array.isArray(fieldValue)) {
                    return fieldValue.map(val => val).join(" - ");
                }
            }
            if (fieldType === "TaxonomyFieldType") {
                return fieldValue?.Label;
            }
            if (fieldType === "TaxonomyFieldTypeMulti") {
                if (Array.isArray(fieldValue)) {
                    return fieldValue.map(val => val.Label).join(" - ");
                }
            }
            if (fieldType === "User" || fieldType === "UserMulti") {
                if (Array.isArray(fieldValue)) {
                    return fieldValue.map(val => val.title).join(" - ");
                }
            }
            if (fieldType === "Lookup" || fieldType === "LookupMulti") {
                if (Array.isArray(fieldValue)) {
                    return fieldValue.map(val => val.lookupValue).join(" - ");
                }
            }
            if (fieldType === "URL") {
                return item[`${field.InternalName}.desc`];
            }
        } catch (error) {
            logger(error, "FileNameDownloadTemplateHelper.ts (getItemFieldTextValue)");
        }
    }
    return null;
}

const getContainerDisplayNames = async (driveId: string, itemId: string): Promise<{ siteDisplayName: string; libraryDisplayName: string; }> => {
    const [siteDisplayName, libraryDisplayName] = await Promise.all([
        (async () => {
            const siteId: string = (await getListItemCustomSelect(driveId, itemId, "parentReference"))?.parentReference?.siteId;
            if (siteId) {
                return (await getSharePointSite(siteId))?.name;
            }
            return null;
        })(),
        (async () => {
            return (await getItemCustomSelect(await authModule.getGraphAccessToken(), driveId, null, "name"))?.name;
        })()
    ]);

    return { siteDisplayName, libraryDisplayName };
}

const getItemDetails = async (siteUrl: string, listId: string, itemUniqueId: string): Promise<{ listFields: any; listItem: any }> => {
    const accessToken: string = await authModule.getSharePointAccessToken();
    const [listFields, listItem] = await Promise.all([
        getListFields(accessToken, siteUrl, listId),
        getItemCurrentVersion(accessToken, siteUrl, listId, itemUniqueId)
    ]);
    return { listFields, listItem };
}